import { ReactNode, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp } from "lucide-react";
import { trackEvent } from "@/lib/trackEvent";

interface CollapsibleWidgetProps {
  /** Stable name used for the widget_expanded analytics event. */
  name: string;
  title: string;
  description: string;
  icon?: ReactNode;
  children: ReactNode;
}

/**
 * Collapsed-by-default shell: shows only a title + one-line description
 * until the visitor expands it. Full widget mounts unchanged on expand.
 */
export function CollapsibleWidget({ name, title, description, icon, children }: CollapsibleWidgetProps) {
  const [open, setOpen] = useState(false);

  const toggle = () => {
    if (!open) trackEvent("widget_expanded", { widget: name });
    setOpen((v) => !v);
  };

  if (open) {
    return (
      <div className="space-y-2">
        {children}
        <div className="flex justify-end">
          <Button variant="ghost" size="sm" className="gap-1 text-xs" onClick={toggle}>
            <ChevronUp className="h-3.5 w-3.5" /> Collapse
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Card className="border-border/50 bg-card/50 backdrop-blur">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-lg">
          {icon}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-wrap items-center justify-between gap-3 pt-0">
        <p className="text-sm text-muted-foreground max-w-xl">{description}</p>
        <Button variant="outline" size="sm" className="gap-1" onClick={toggle}>
          Expand <ChevronDown className="h-4 w-4" />
        </Button>
      </CardContent>
    </Card>
  );
}
